/**
 * One voice for every "the person you called is not here" outcome.
 *
 * The server sends the same p2p_target_offline shape for three different
 * situations and the app used to blur them into "did not answer":
 *
 *   - reason 'no_answer'    the call rang for the full window and nobody
 *                           picked up. deliveryReason says whether their
 *                           device was ever alerted.
 *   - reason 'do_not_disturb' (p2p_target_dnd)
 *   - anything else         the server could not even start the ring.
 *
 * The distinction matters to the caller ("Devin ignored me" vs "Devin's
 * phone never rang") and to ops: 'notification_provider_not_configured' is
 * a server-side misconfiguration, not a person being away. The caller still
 * gets person-level words for it — what happened to their call, not which
 * part of our infrastructure is missing; ops reads the deliveryReason code.
 */
const DELIVERY_DETAIL = {
    no_registered_device: 'Their phone is not set up for call alerts.',
    notification_provider_not_configured: 'Their phone did not ring.',
    notification_delivery_failed: 'We could not alert their phone.',
    notification_rate_limited: 'Call alerts to them are paused for a moment.',
    notification_dispatch_unavailable: 'Their phone may not have rung.',
    recipient_offline: 'They were not online.'
};
export function describeP2PUnavailable(data, appName = 'the app') {
    const name = data?.calleeName || 'That contact';
    const isGroup = data?.groupCall === true || (data?.participantCount || 0) > 2;
    const reason = data?.reason || '';
    const deliveryReason = data?.deliveryReason || '';
    const detail = deliveryReason && deliveryReason !== 'no_answer' ? DELIVERY_DETAIL[deliveryReason] || '' : '';
    if (reason === 'do_not_disturb' || reason === 'all_recipients_dnd') {
        const title = isGroup ? 'Everyone has Do Not Disturb on' : `${name} has Do Not Disturb on`;
        const note = !isGroup && typeof data?.dndMessage === 'string' ? data.dndMessage.trim().slice(0, 200) : '';
        const detail = note ? `Their message: “${note}”` : '';
        return { detail, message: `${title}.${detail ? ` ${detail}` : ''}`, title };
    }
    if (reason === 'no_answer') {
        const title = `${name} didn't answer`;
        return {
            detail,
            message: `${title}.${detail ? ` ${detail}` : ''}`,
            title
        };
    }
    if (isGroup) {
        const title = `None of the selected ${appName} contacts is available right now`;
        return { detail, message: `${title}.`, title };
    }
    const title = `${name} could not be reached right now`;
    return { detail, message: `${title}.${detail ? ` ${detail}` : ''}`, title };
}
/** Whether the callee could not take the call because of Do Not Disturb. */
export function isDoNotDisturbOutcome(reason) {
    return reason === 'do_not_disturb' || reason === 'all_recipients_dnd';
}
/**
 * The caller's Do Not Disturb card (web: showCalleeUnavailable in
 * client-profile.js): who cannot take calls, and the callee's own message.
 */
export function describeCalleeDnd(data) {
    const name = data?.calleeName || 'That contact';
    const note = typeof data?.dndMessage === 'string' ? data.dndMessage.trim().slice(0, 200) : '';
    return {
        detail: note ? `They have Do Not Disturb on. Their message: “${note}”` : 'They have Do Not Disturb on.',
        greetingCaption: 'Their VideoMail greeting',
        greetingLabel: `${name}’s VideoMail greeting`,
        title: `${name} can’t take calls right now`
    };
}
/** Status line while an outgoing direct call is ringing. */
export function describeP2PRinging(data) {
    if (data?.delivery === 'unconfirmed') {
        return `Trying to reach ${data?.calleeName || 'them'}…`;
    }
    return 'Ringing…';
}
